import React, { useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { toPng } from 'html-to-image';
import InstagramPromoBanner, {
  BannerVehicleItem,
  BANNER_WIDTH,
} from './InstagramPromoBanner';
import { STORE_BRANDING } from '../../../config/storeBranding';
import { resolveImageDataUrl } from '../../../utils/resolveImageDataUrl';

export type BannerSourceVehicle = {
  id: number;
  brand: string;
  model: string;
  year: number;
  sale_price?: number;
  sale_price_promo?: number;
  images?: { id: number; url: string }[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  vehicles: BannerSourceVehicle[];
  getImageUrl: (url?: string) => string | null;
};

const MAX_VEHICLES = 4;

const formatDateHeadline = (from: string, to: string): string => {
  if (!from && !to) return 'OFERTAS POR TEMPO LIMITADO';
  const parse = (iso: string) => {
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return null;
    const months = [
      'JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO',
      'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO',
    ];
    return { d: String(Number(d)), month: months[Number(m) - 1] || m, y };
  };
  const a = parse(from);
  const b = parse(to || from);
  if (!a || !b) return 'OFERTAS POR TEMPO LIMITADO';
  if (a.month === b.month && a.y === b.y) {
    if (a.d === b.d) return `DIA ${a.d} DE ${a.month}`;
    return `DIAS ${a.d} E ${b.d} DE ${a.month}`;
  }
  return `${a.d} DE ${a.month} A ${b.d} DE ${b.month}`;
};

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = parseFloat(String(v ?? '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const buildItems = (
  selected: BannerSourceVehicle[],
  getImageUrl: (url?: string) => string | null,
  images?: Record<number, string | null>,
  preferInlinedOnly = false
): BannerVehicleItem[] =>
  selected.map((v) => {
    const sale = toNum(v.sale_price);
    const promo = toNum(v.sale_price_promo);
    const hasPromo = promo > 0 && (sale <= 0 || promo < sale);
    const priceTo = hasPromo ? promo : sale > 0 ? sale : 0;
    const priceFrom = hasPromo && sale > promo ? sale : null;
    const original = getImageUrl(v.images?.[0]?.url);
    const inlined = images ? images[v.id] : undefined;
    let imageUrl: string | null = original;
    if (preferInlinedOnly) {
      // Export: só data URL (S3 externo quebra o toPng por CORS)
      imageUrl = inlined || null;
    } else if (inlined) {
      imageUrl = inlined;
    }
    return {
      id: v.id,
      title: `${v.brand} ${v.model} ${String(v.year).slice(-2)}`.toUpperCase(),
      brand: v.brand,
      model: v.model,
      year: v.year,
      imageUrl,
      priceFrom,
      priceTo,
    };
  });

const InstagramBannerDialog: React.FC<Props> = ({
  open,
  onClose,
  vehicles,
  getImageUrl,
}) => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const [titleMain, setTitleMain] = useState('48H');
  const [titleSub, setTitleSub] = useState('DE VENDAS');
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [subtitle, setSubtitle] = useState(
    `Condições especiais para você sair de carro com a ${STORE_BRANDING.name}`
  );
  const [exporting, setExporting] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [corsWarning, setCorsWarning] = useState(false);
  /** Só data URLs (ou null se falhou). Preview usa URL original quando null. */
  const [exportImages, setExportImages] = useState<Record<number, string | null>>({});

  const selected = vehicles.slice(0, MAX_VEHICLES);
  const selectedKey = selected.map((v) => v.id).join(',');

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const run = async () => {
      setPreparing(true);
      setCorsWarning(false);
      setError(null);
      const next: Record<number, string | null> = {};
      let failed = 0;

      await Promise.all(
        selected.map(async (v) => {
          const src = getImageUrl(v.images?.[0]?.url);
          if (!src) {
            next[v.id] = null;
            return;
          }
          const dataUrl = await resolveImageDataUrl(src);
          next[v.id] = dataUrl;
          if (!dataUrl) failed += 1;
        })
      );

      if (!cancelled) {
        setExportImages(next);
        setCorsWarning(failed > 0);
        setPreparing(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedKey]);

  const previewVehicles = useMemo(
    () => buildItems(selected, getImageUrl),
    [selected, getImageUrl]
  );

  const exportVehicles = useMemo(
    () => buildItems(selected, getImageUrl, exportImages, true),
    [selected, getImageUrl, exportImages]
  );

  const photosReadyCount = selected.filter((v) => {
    const hasSrc = !!getImageUrl(v.images?.[0]?.url);
    if (!hasSrc) return true; // sem foto cadastrada — ok
    return !!exportImages[v.id];
  }).length;
  const allPhotosReady = photosReadyCount === selected.length && !preparing;

  const dateLabel = formatDateHeadline(dateFrom, dateTo);

  const waitForImages = (root: HTMLElement) =>
    Promise.all(
      Array.from(root.querySelectorAll('img')).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }
            const done = () => resolve();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
            // timeout de segurança
            setTimeout(done, 8000);
          })
      )
    );

  const handleDownload = async () => {
    if (!bannerRef.current) return;
    setExporting(true);
    setError(null);
    try {
      const next: Record<number, string | null> = {};
      let failed = 0;
      const missingNames: string[] = [];

      await Promise.all(
        selected.map(async (v) => {
          const src = getImageUrl(v.images?.[0]?.url);
          if (!src) {
            next[v.id] = null;
            return;
          }
          const dataUrl = await resolveImageDataUrl(src);
          next[v.id] = dataUrl;
          if (!dataUrl) {
            failed += 1;
            missingNames.push(`${v.brand} ${v.model}`);
          }
        })
      );

      if (failed > 0) {
        setCorsWarning(true);
        setError(
          `Não foi possível carregar a foto de: ${missingNames.join(', ')}. Tente de novo em alguns segundos.`
        );
        flushSync(() => setExportImages(next));
        return;
      }

      flushSync(() => {
        setExportImages(next);
        setCorsWarning(false);
      });

      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (typeof document !== 'undefined' && document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch {
          /* ignore */
        }
      }
      await waitForImages(bannerRef.current);
      await new Promise((r) => setTimeout(r, 250));

      const dataUrl = await toPng(bannerRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        width: BANNER_WIDTH,
        backgroundColor: '#ffffff',
        filter: (node) => {
          if (node instanceof HTMLImageElement) {
            const src = node.getAttribute('src') || '';
            // Só permite data: ou mesma origem — nunca S3 cru no clone
            if (/^https?:\/\//i.test(src) && src.includes('amazonaws.com')) {
              return false;
            }
          }
          return true;
        },
      });

      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `banner-instagram-${STORE_BRANDING.name
        .toLowerCase()
        .replace(/\s+/g, '-')}-${dateFrom}.png`;
      a.click();
    } catch (e) {
      console.error(e);
      setError(
        'Falha ao gerar o PNG. Tente novamente. Se persistir, configure CORS no S3 ou a rota /api/tools/image-proxy.'
      );
    } finally {
      setExporting(false);
    }
  };

  const previewScale = 0.32;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Banner Instagram — {STORE_BRANDING.name}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Selecione até <strong>{MAX_VEHICLES} veículos</strong>. Use preço promocional
            (`sale_price_promo`) para aparecer “De / Por”. O arquivo sai em PNG alto para postar no Instagram.
          </Alert>

          {vehicles.length > MAX_VEHICLES && (
            <Alert severity="warning">
              {vehicles.length} selecionados — só os {MAX_VEHICLES} primeiros entram no banner.
            </Alert>
          )}

          {preparing && (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
              Carregando fotos dos veículos para o PNG…
            </Alert>
          )}

          {!preparing && allPhotosReady && selected.length > 0 && (
            <Alert severity="success">
              {selected.length} foto(s) pronta(s) para o banner. Pode baixar o PNG.
            </Alert>
          )}

          {corsWarning && (
            <Alert severity="warning">
              Não conseguimos ler {selected.length - photosReadyCount} foto(s). Clique em{' '}
              <strong>Baixar PNG</strong> de novo para tentar outra vez, ou confira se o veículo tem
              imagem cadastrada.
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Título grande"
              value={titleMain}
              onChange={(e) => setTitleMain(e.target.value)}
              fullWidth
              helperText="Ex.: 48H, FEIRÃO, BLACK"
            />
            <TextField
              label="Subtítulo"
              value={titleSub}
              onChange={(e) => setTitleSub(e.target.value)}
              fullWidth
              helperText="Ex.: DE VENDAS"
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Data início"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Data fim"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          <TextField
            label="Frase de apoio"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <Typography variant="subtitle2" color="text.secondary">
            Preview ({dateLabel})
          </Typography>

          {/* Off-screen: usa data URLs quando disponíveis (export) */}
          <Box
            aria-hidden
            sx={{
              position: 'fixed',
              left: -12000,
              top: 0,
              pointerEvents: 'none',
              opacity: 0,
            }}
          >
            <InstagramPromoBanner
              ref={bannerRef}
              titleMain={titleMain}
              titleSub={titleSub}
              dateLabel={dateLabel}
              subtitle={subtitle}
              vehicles={exportVehicles}
            />
          </Box>

          <Box
            sx={{
              overflow: 'auto',
              maxHeight: 520,
              bgcolor: 'rgba(0,0,0,0.04)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: BANNER_WIDTH * previewScale, overflow: 'hidden' }}>
              <Box
                sx={{
                  width: BANNER_WIDTH,
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                }}
              >
                <InstagramPromoBanner
                  titleMain={titleMain}
                  titleSub={titleSub}
                  dateLabel={dateLabel}
                  subtitle={subtitle}
                  vehicles={previewVehicles}
                />
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={exporting}>
          Fechar
        </Button>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={() => void handleDownload()}
          disabled={exporting || preparing || exportVehicles.length === 0}
        >
          {exporting ? 'Gerando PNG com fotos...' : preparing ? 'Carregando fotos...' : 'Baixar PNG'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstagramBannerDialog;
